import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ENDPOINTS } from "@/lib/api";

type FetchState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ok"; ms: number; payload: unknown }
  | { kind: "error"; message: string };

/**
 * The category-first move: a button that lets any visitor pull a live signed
 * price directly from the attestation service and see the raw JSON. No
 * wallet, no SDK, no setup — just the actual signed bytes from production.
 */
export function PullSignatureButton() {
  const [state, setState] = useState<FetchState>({ kind: "idle" });

  async function pull() {
    setState({ kind: "loading" });
    const t0 = performance.now();
    try {
      const res = await fetch(ENDPOINTS.latest, { cache: "no-store" });
      const body = await res.json();
      const ms = Math.round(performance.now() - t0);
      // Trim to a single asset so the payload is readable. BTC if available.
      const trimmed = {
        round_id: body?.assets?.["BTC/USD"]?.round_id,
        asset: "BTC/USD",
        ...(body?.assets?.["BTC/USD"] ?? {}),
      };
      setState({ kind: "ok", ms, payload: trimmed });
    } catch (err) {
      setState({
        kind: "error",
        message: err instanceof Error ? err.message : "Network error",
      });
    }
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={pull}
        disabled={state.kind === "loading"}
        className="group inline-flex items-center gap-3 rounded-lg border border-emerald/40 bg-emerald/[0.04] px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-emerald transition-all hover:bg-emerald/[0.08] hover:border-emerald/70 disabled:opacity-50"
        aria-label="Pull a live signed price from the attestation service"
      >
        <span className="relative inline-flex size-2 items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-emerald opacity-80" />
          {state.kind === "loading" && (
            <span className="absolute -inset-1 rounded-full border border-emerald/60 animate-ping" />
          )}
        </span>
        {state.kind === "loading" ? "Pulling…" : "Pull a signature"}
        <span className="text-emerald/60 group-hover:translate-x-0.5 transition-transform">↓</span>
      </button>

      <AnimatePresence>
        {state.kind === "ok" && (
          <motion.div
            key="ok"
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-4 rounded-lg border border-border bg-surface/60 overflow-hidden">
              <div className="flex items-center justify-between gap-2 border-b border-border bg-bg-deep/60 px-4 py-2">
                <span className="font-mono text-[9.5px] uppercase tracking-[0.24em] text-m45">
                  <span className="text-emerald mr-2">●</span>
                  GET {ENDPOINTS.latest.replace("https://", "")}
                </span>
                <span className="font-mono text-[9.5px] uppercase tracking-[0.24em] text-m45">
                  {state.ms}ms · <span className="text-emerald">200 OK</span>
                </span>
              </div>
              <pre className="overflow-x-auto px-4 py-3 font-mono text-[11.5px] leading-relaxed text-text">
                <code>{prettyJson(state.payload)}</code>
              </pre>
            </div>
          </motion.div>
        )}
        {state.kind === "error" && (
          <motion.div
            key="err"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="mt-3 rounded-md border border-red/30 bg-red/[0.05] px-4 py-2 font-mono text-[11px] text-red"
          >
            ⚠ {state.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function prettyJson(value: unknown): string {
  try {
    const raw = JSON.stringify(value, null, 2);
    // Syntax-color via simple markers — keep markup-free, we color in CSS later.
    return raw;
  } catch {
    return String(value);
  }
}
